import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import type { NewSupplierDraft } from './SupplierList.types'

export interface NewSupplierRequestDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (draft: NewSupplierDraft) => void
  typeOptions: string[]
  oemOptions: string[]
}

export function NewSupplierRequestDialog({
  open,
  loading,
  onClose,
  onSubmit,
  typeOptions,
  oemOptions,
}: {
  open: boolean
  loading: boolean
  onClose: () => void
  onSubmit: (draft: NewSupplierDraft) => void
  typeOptions: string[]
  oemOptions: string[]
}) {
  const [draft, setDraft] = useState<NewSupplierDraft>({
    name: '',
    type: '',
    blanket: false,
    requestedOems: [],
    defaultNetsuiteVendor: '',
  })

  // Reset draft each time dialog opens
  useEffect(() => {
    if (!open) return
    setDraft({
      name: '',
      type: '',
      blanket: false,
      requestedOems: [],
      defaultNetsuiteVendor: '',
    })
  }, [open])

  const canSubmit = useMemo(() => {
    const nameOk = draft.name.trim().length > 0
    const typeOk = draft.type.trim().length > 0
    const oemOk = draft.blanket ? true : draft.requestedOems.length > 0
    return nameOk && typeOk && oemOk
  }, [draft.name, draft.type, draft.blanket, draft.requestedOems])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight={600}>New Supplier Request</DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" marginBottom={2}>
          All fields are required.
        </Typography>

        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Name"
            size="small"
            value={draft.name}
            onChange={e => setDraft(prev => ({ ...prev, name: e.target.value }))}
            fullWidth
          />

          <TextField
            label="Type"
            size="small"
            select
            value={draft.type}
            onChange={e => setDraft(prev => ({ ...prev, type: e.target.value }))}
            fullWidth
          >
            <MenuItem value="">
              <em>Type</em>
            </MenuItem>
            {typeOptions.map(t => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>

          <FormControlLabel
            label="Blanket (Authorizes use of any manufacturer with the supplier)"
            control={
              <Checkbox
                checked={draft.blanket}
                onChange={e => {
                  const checked = e.target.checked
                  setDraft(prev => ({
                    ...prev,
                    blanket: checked,
                    requestedOems: checked ? [] : prev.requestedOems, // clear when blanket on
                  }))
                }}
              />
            }
          />

          {!draft.blanket && (
            <Autocomplete
              multiple
              options={oemOptions}
              value={draft.requestedOems}
              onChange={(_e, newValue) => setDraft(prev => ({ ...prev, requestedOems: newValue }))}
              filterSelectedOptions
              disableCloseOnSelect
              renderValue={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option} size="small" {...getTagProps({ index })} key={getTagProps({ index }).key} />
                ))
              }
              renderInput={params => <TextField {...params} label="Requested OEM" size="small" />}
            />
          )}

          <Box
            border="2px dashed rgba(0,0,0,0.2)"
            borderRadius={1}
            padding={3}
            textAlign="center"
            display="flex"
            flexDirection="column"
            gap={1}
          >
            <Typography fontWeight={600}>Drop Files to Upload</Typography>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
            <Box>
              <Button variant="contained" size="small" disabled>
                CHOOSE FILES
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          CANCEL
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() => {
            onSubmit(draft)
          }}
          loading={loading}
        >
          SUBMIT
        </Button>
      </DialogActions>
    </Dialog>
  )
}
